<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminBlanketVOBList" label="Blanket VOB Searches" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Blanket VOB Search List</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-switch :left-label="true" v-model="showAll">Show All Blanket VOB Searches</b-switch>
          </div>
        </div>
      </div>
      <hr />

      <!-- Filter Section -->
      <div class="filter-section">
        <div class="columns is-multiline">
          <div class="column is-3">
            <b-field label="Organization">
              <b-select
                v-model="selectedOrganizationID"
                placeholder="All Organizations"
                :loading="loadingOrganizations"
                @input="handleOrganizationChange"
                expanded
              >
                <option :value="0">All Organizations</option>
                <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field label="Provider">
              <b-select
                v-model="selectedProviderID"
                placeholder="All Providers"
                :loading="loadingProviders"
                :disabled="selectedOrganizationID === 0"
                @input="handleProviderChange"
                expanded
              >
                <option :value="0">All Providers</option>
                <option v-for="provider in providerOptions" :value="provider.id" :key="provider.id">
                  {{ provider.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field label="User">
              <b-select
                v-model="selectedUserID"
                placeholder="All Users"
                :loading="loadingUsers"
                :disabled="selectedOrganizationID === 0"
                @input="handleUserChange"
                expanded
              >
                <option :value="0">All Users</option>
                <option v-for="user in userOptions" :value="user.id" :key="user.id">
                  {{ user.first_name }} {{ user.last_name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <div class="search-section">
        <b-field>
          <div class="field-body">
            <b-input
              class="is-expanded"
              :loading="loading"
              v-model="search"
              placeholder="Search by First or last name, Member ID, or Payer, Group Name"
              @input="handleSearch"
            />
            <b-dropdown
              class="filter-dropdown"
              position="is-bottom-left"
              v-model="searchableFields"
              multiple
              aria-role="list"
            >
              <template #trigger>
                <b-button icon-right="filter" type="is-primary">
                  Search Fields ({{ searchableFields.length }})
                </b-button>
              </template>

              <b-dropdown-item
                v-for="field in allFields"
                :key="field.value"
                :value="field.value"
                aria-role="listitem"
              >
                <div @click="toggleCheckbox(field.value)">
                  <b-checkbox
                    v-model="searchableFields"
                    :native-value="field.value"
                    class="checkbox-left"
                  >
                    {{ field.label }}
                  </b-checkbox>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-field>
      </div>

      <div>
        <BlanketVOBTable
          :options="options"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-items="totalItems"
          :loading="loading"
          :admin-mode="true"
          @page-change="handlePageChange"
          @search-changed="handleSearchChange"
        />
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import BlanketVOBTable from '../../components/BlanketVOBTable.vue';
import AdminLayout from '../../layouts/AdminLayout.vue';
import request from '../../request';
import { sanitizeSearchInput } from '../../utils/form';

export default {
  name: 'AdminBlanketVOBList',
  components: {
    BlanketVOBTable,
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      options: [],
      showAll: false,
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      search: '',
      searchableFields: ['patient_first_name', 'patient_last_name', 'member_id', 'payer_name', 'group_name'],
      allFields: [
        { value: 'patient_first_name', label: 'First Name' },
        { value: 'patient_last_name', label: 'Last Name' },
        { value: 'member_id', label: 'Member ID' },
        { value: 'payer_name', label: 'Payer Name' },
        { value: 'group_name', label: 'Group Name' },

      ],
      // Filter options
      selectedOrganizationID: 0,
      selectedProviderID: 0,
      selectedUserID: 0,
      organizationOptions: [],
      providerOptions: [],
      userOptions: [],
      loadingOrganizations: false,
      loadingProviders: false,
      loadingUsers: false,
    };
  },
  mounted() {
    this.fetchOrganizations();
    this.fetchBlanketVOBs();
  },
  methods: {
    async fetchOrganizations() {
      this.loadingOrganizations = true;
      try {
        const resp = await request('/admin/organizations');
        this.organizationOptions = resp.data.organizations.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load organizations.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingOrganizations = false;
      }
    },
    async fetchProviders(organizationId) {
      this.loadingProviders = true;
      this.providerOptions = [];
      this.selectedProviderID = 0;

      if (organizationId === 0) {
        this.loadingProviders = false;
        return;
      }

      try {
        const resp = await request(`/admin/providers?organization_id=${organizationId}`);
        this.providerOptions = resp.data.providers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load providers.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingProviders = false;
      }
    },
    async fetchUsers(organizationId) {
      this.loadingUsers = true;
      this.userOptions = [];
      this.selectedUserID = 0;

      if (organizationId === 0) {
        this.loadingUsers = false;
        return;
      }

      try {
        const resp = await request(`/admin/users?organization_id=${organizationId}`);
        this.userOptions = resp.data.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load users.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    handleOrganizationChange() {
      this.fetchProviders(this.selectedOrganizationID);
      this.fetchUsers(this.selectedOrganizationID);
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
    handleProviderChange() {
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
    handleUserChange() {
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
    toggleCheckbox(value) {
      const index = this.searchableFields.indexOf(value);
      if (index === -1) {
        this.searchableFields.push(value);
      } else {
        this.searchableFields.splice(index, 1);
      }
      this.handleSearch();
    },
    fetchBlanketVOBs() {
      this.loading = true;
      const args = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      if (this.showAll) {
        args.include_hidden = 'true';
      }

      // Add filter parameters
      if (this.selectedOrganizationID !== 0) {
        args.organization_id = this.selectedOrganizationID;
      }
      if (this.selectedProviderID !== 0) {
        args.provider_id = this.selectedProviderID;
      }
      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }

      // Add search parameters if search term exists
      if (this.search && this.search.trim()) {
        const sanitizedSearch = sanitizeSearchInput(this.search);
        this.searchableFields.forEach((field) => {
          args[field] = sanitizedSearch;
        });
      }

      const params = new URLSearchParams(args).toString();
      return request(`/admin/blanket-vob-search/?${params}`)
        .then((resp) => {
          this.options = resp.data.list;
          const totalCount = resp.headers['x-total-count'];
          if (totalCount) {
            this.totalItems = parseInt(totalCount, 10);
          } else {
            this.totalItems = resp.data.list.length;
          }
          this.loading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred while fetching blanket VOB searches.',
              type: 'is-danger',
              duration: 5000,
            });
          }
          this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchBlanketVOBs();
    },
    handleSearchChange({ search, fields }) {
      this.search = sanitizeSearchInput(search);
      this.searchableFields = fields;
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
  },
  watch: {
    showAll() {
      this.currentPage = 1;
      this.fetchBlanketVOBs();
    },
  },
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.search-section {
  margin-bottom: 1rem;
}
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}
</style>
